<template>
  <!-- Mobile Layout -->
  <div v-if="isMobile" class="relative w-full bg-white transition-colors duration-200" :class="[
    isOpen ? 'rounded-[25px] rounded-b-2xl' : 'rounded-full',
    variant === 'light' ? 'border border-gray-200' : 'border border-[#2F3033]'
  ]">

      <button
        type="button"
        @click="toggleAccordion"
        :class="[
          isOpen ? 'rounded-t-[25px] rounded-b-none' : 'rounded-full',
          compact ? 'h-11' : 'h-[50px]'
        ]"
        class="w-full px-4 flex items-center justify-between text-left hover:bg-white min-h-[44px] transition-colors duration-200"
      >
        <div class="flex items-center justify-between w-full">
          <div class="text-lg text-left flex-1 font-semibold text-[#2F3033]">{{ displayText }}</div>
          <Icon 
            name="chevron-down"
            class="text-gray-300 transition-transform duration-200 accordion-arrow w-4 h-4" 
            :class="{ 'rotate-180': isOpen }"
          />
        </div>
      </button>
      
      <div v-if="isOpen" class="border-t border-gray-200 text-lg">
        <div class="p-4 space-y-6">
          <!-- Adults -->
          <div class="flex items-center justify-between">
            <div>
              <div class="text-lg text-gray-900  ">Adults</div>
              <div class="text-sm text-gray-500  ">12yrs +</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                @click.stop="decrementTravelers('adults')"
                :disabled="modelValue.adults <= 0"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-5 h-5" />
              </button>
              <span class="text-lg text-gray-900   min-w-[24px] text-center">
                {{ modelValue.adults }}
              </span>
              <button
                type="button"
                @click.stop="incrementTravelers('adults')"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-5 h-5" />
              </button>
            </div>
          </div>
  
          <!-- Children -->
          <div class="flex items-center justify-between">
            <div>
              <div class="text-lg text-gray-900  ">Children</div>
              <div class="text-sm text-gray-500  ">2yrs - 11yrs</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                @click.stop="decrementTravelers('children')"
                :disabled="modelValue.children <= 0"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-5 h-5" />
              </button>
              <span class="text-lg text-gray-900   min-w-[24px] text-center">
                {{ modelValue.children }}
              </span>
              <button
                @click.stop="incrementTravelers('children')"
                type="button"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-5 h-5" />
              </button>
            </div>
          </div>
  
          <!-- Pets -->
          <div class="flex items-center justify-between">
            <div>
              <div class="text-lg text-gray-900  ">Pets</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                @click.stop="decrementTravelers('pets')"
                :disabled="modelValue.pets <= 0"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-5 h-5" />
              </button>
              <span class="text-lg text-gray-900   min-w-[24px] text-center">
                {{ modelValue.pets }}
              </span>
              <button
                @click.stop="incrementTravelers('pets')"
                type="button"
                class="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
  </div>

  <!-- Desktop Layout -->
  <div v-else ref="accordionRef" class="relative flex-shrink-0 w-full md:min-w-[270px]">
      <div class="relative rounded-3xl w-full transition-all shadow-sm bg-white" :class="variant === 'light' ? 'border border-gray-200' : 'border border-[#2F3033]'">
        <!-- Travellers Label (Desktop) -->
        <div v-if="!hideLabel" class="absolute -top-3 right-3 z-[70]">
          <span :class="showError ? 'bg-red-50 text-red-500 border-red-200' : 'bg-[#e0f7ef] text-[#4ECF84] border-gray-200'" class="text-xs font-medium px-3 py-1 rounded-[20px] border">Add Travellers</span>
        </div>
        <button
          type="button"
          @click="toggleAccordion"
          :class="compact ? 'h-11' : 'h-[50px]'"
          class="w-full px-4 flex items-center justify-between text-left transition-colors focus:outline-none"
        >
          <div class="text-base font-semibold whitespace-nowrap text-left flex-1 text-[#2F3033]" style="font-family: 'Raleway', sans-serif;">
            {{ displayText }}
          </div>
          <Icon 
            name="chevron-down"
            class="text-[#2F3033] transition-transform duration-200 w-5 h-5 flex-shrink-0"
            style="stroke-width: 2;"
            :class="{ 'rotate-180': isOpen }"
          />
      </button>

      <!-- Desktop Accordion Content -->
      <div
        v-if="isOpen"
        :class="[
          inlineDropdown
            ? 'relative z-[10] mt-1 w-full min-w-[250px]'
            : 'fixed z-[100] bg-white rounded-3xl shadow-xl border border-gray-200 mt-1 w-full min-w-[250px]'
        ]"
        :style="inlineDropdown ? {} : dropdownStyle"
      >
        <div class="px-4 pb-4 pt-4 space-y-6">
          <!-- Adults -->
          <div class="flex items-center justify-between">
            <div class="pl-2">
              <div class="text-base font-semibold text-[#2F3033]">Adults</div>
              <div class="text-sm text-gray-500">12yrs +</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                type="button"
                @click.stop="decrementTravelers('adults')"
                :disabled="modelValue.adults <= 0"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-4 h-4" />
              </button>
              <span class="text-base font-semibold text-[#2F3033] min-w-[20px] text-center">
                {{ modelValue.adults }}
              </span>
              <button
                type="button"
                @click.stop="incrementTravelers('adults')"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Children -->
          <div class="flex items-center justify-between">
            <div class="pl-2">
              <div class="text-base font-semibold text-[#2F3033]">Children</div>
              <div class="text-sm text-gray-500">2yrs - 11yrs</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                type="button"
                @click.stop="decrementTravelers('children')"
                :disabled="modelValue.children <= 0"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-4 h-4" />
              </button>
              <span class="text-base font-semibold text-[#2F3033] min-w-[20px] text-center">
                {{ modelValue.children }}
              </span>
              <button
                type="button"
                @click.stop="incrementTravelers('children')"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Pets -->
          <div class="flex items-center justify-between">
            <div class="pl-2">
              <div class="text-base font-semibold text-[#2F3033]">Pets</div>
            </div>
            <div class="flex items-center gap-4">
              <button
                type="button"
                @click.stop="decrementTravelers('pets')"
                :disabled="modelValue.pets <= 0"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="minus" class="w-4 h-4" />
              </button>
              <span class="text-base font-semibold text-[#2F3033] min-w-[20px] text-center">{{ modelValue.pets }}</span>
              <button
                type="button"
                @click.stop="incrementTravelers('pets')"
                class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <Icon name="plus" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import Icon from './Icon.vue'

// Component name
const componentName = 'Travellers'

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
    validator: (value) => {
      return typeof value === 'object' && 
             typeof value.adults === 'number' && 
             typeof value.children === 'number' && 
             typeof value.pets === 'number'
    }
  },
  isMobile: {
    type: Boolean,
    default: false
  },
  inlineDropdown: {
    type: Boolean,
    default: false
  },
  showError: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  hideLabel: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'dark',
    validator: (v) => ['dark', 'light'].includes(v)
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'dropdown-open', 'dropdown-close'])

// Local state
const isOpen = ref(false)
const accordionRef = ref(null)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })

// Computed properties
const displayText = computed(() => {
  const parts = []
  if (props.modelValue.adults > 0) {
    parts.push(`${props.modelValue.adults} Adult${props.modelValue.adults > 1 ? 's' : ''}`)
  }
  if (props.modelValue.children > 0) {
    parts.push(`${props.modelValue.children} Child${props.modelValue.children > 1 ? 'ren' : ''}`)
  }
  if (props.modelValue.pets > 0) {
    parts.push(`${props.modelValue.pets} Pet${props.modelValue.pets > 1 ? 's' : ''}`)
  }
  
  if (parts.length === 0) {
    return 'Add Travellers'
  }
  
  return parts.join(', ')
})

const isPlaceholder = computed(() => {
  return props.modelValue.adults === 0 && props.modelValue.children === 0 && props.modelValue.pets === 0
})

const dropdownStyle = computed(() => {
  if (props.inlineDropdown) return {}
  if (!props.isMobile && dropdownPosition.value) {
    return {
      top: `${dropdownPosition.value.top}px`,
      left: `${dropdownPosition.value.left}px`,
      width: `${dropdownPosition.value.width}px`
    }
  }
  return {}
})

// Methods
const updateDropdownPosition = () => {
  if (props.inlineDropdown) return
  if (!props.isMobile && accordionRef.value) {
    const rect = accordionRef.value.getBoundingClientRect()
    dropdownPosition.value = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 250)
    }
  }
}

const toggleAccordion = async () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    await nextTick()
    updateDropdownPosition()
    emit('dropdown-open')
  } else {
    emit('dropdown-close')
  }
}

// Method to close dropdown (called from parent)
const closeDropdown = () => {
  if (isOpen.value) {
    isOpen.value = false
    emit('dropdown-close')
  }
}

const incrementTravelers = (type) => {
  const newValue = { ...props.modelValue }
  newValue[type]++
  emit('update:modelValue', newValue)
}

const decrementTravelers = (type) => {
  const newValue = { ...props.modelValue }
  const minimum = 0
  if (newValue[type] > minimum) {
    newValue[type]--
  }
  emit('update:modelValue', newValue)
}

// Click outside functionality for desktop
const handleClickOutside = (event) => {
  if (!props.isMobile && accordionRef.value && !accordionRef.value.contains(event.target)) {
    isOpen.value = false
    emit('dropdown-close')
  }
}

onMounted(() => {
  if (!props.isMobile) {
    document.addEventListener('click', handleClickOutside)
    if (!props.inlineDropdown) {
      window.addEventListener('resize', updateDropdownPosition)
      window.addEventListener('scroll', updateDropdownPosition)
    }
  }
})

onUnmounted(() => {
  if (!props.isMobile) {
    document.removeEventListener('click', handleClickOutside)
    if (!props.inlineDropdown) {
      window.removeEventListener('resize', updateDropdownPosition)
      window.removeEventListener('scroll', updateDropdownPosition)
    }
  }
})

// Expose methods for parent component
defineExpose({
  closeDropdown
})
</script>

<style scoped>
.accordion-arrow {
  transition: transform 0.2s ease-in-out;
}

/* Ensure standard mobile input size to prevent iOS zoom (mobile only) */
@media (max-width: 767px) {
  button {
    -webkit-appearance: none;
    appearance: none;
  }
}
</style>
